((root, factory) => {
	if (typeof module !== "undefined" && module.exports) {
		module.exports = factory();
	} else {
		factory();
	}
})(
	typeof globalThis !== "undefined"
		? globalThis
		: typeof window !== "undefined"
			? window
			: this,
	() => {
		// ── Pure helpers (no browser dependencies) ──

		function normalizeApiBaseUrl(value) {
			return String(value || "").replace(/\/+$/, "");
		}

		function buildCheckoutStorageKey(sessionId) {
			return "ardexa_checkout_token:" + sessionId;
		}

		function buildIdempotencyStorageKey(cartToken) {
			return "ardexa_checkout_idempotency:" + (cartToken || "anonymous");
		}

		function buildCheckoutPath(pagePath, sessionId, baseOrigin) {
			if (baseOrigin === undefined && typeof window !== "undefined") {
				baseOrigin = window.location.origin;
			} else if (baseOrigin === undefined) {
				baseOrigin = "https://www.ardexa.it";
			}
			var url;
			try {
				url = new URL(pagePath || "/pages/checkout-ardexa", baseOrigin);
			} catch (e) {
				url = new URL("/pages/checkout-ardexa", baseOrigin);
			}
			if (url.origin !== baseOrigin) {
				url = new URL("/pages/checkout-ardexa", baseOrigin);
			}
			url.searchParams.set("sid", sessionId);
			return url.pathname + url.search;
		}

		function getSessionIdFromUrl(href, baseOrigin) {
			if (baseOrigin === undefined && typeof window !== "undefined") {
				baseOrigin = window.location.origin;
			} else if (baseOrigin === undefined) {
				baseOrigin = "https://www.ardexa.it";
			}
			if (href === undefined && typeof window !== "undefined") {
				href = window.location.href;
			} else if (href === undefined) {
				href = "";
			}
			try {
				return new URL(href, baseOrigin).searchParams.get("sid");
			} catch (e) {
				return null;
			}
		}

		// ── Node.js / CommonJS export ──

		if (typeof module !== "undefined" && module.exports) {
			return {
				normalizeApiBaseUrl: normalizeApiBaseUrl,
				buildCheckoutStorageKey: buildCheckoutStorageKey,
				buildIdempotencyStorageKey: buildIdempotencyStorageKey,
				buildCheckoutPath: buildCheckoutPath,
				getSessionIdFromUrl: getSessionIdFromUrl,
			};
		}

		// ── Browser-only code (preserves original behavior) ──

		var config = window.ardexaCheckout || {};

		const getIdempotencyKey = (cart) => {
			const storageKey = buildIdempotencyStorageKey(cart?.token);
			const existing = window.sessionStorage.getItem(storageKey);
			if (existing) return existing;
			const key = window.crypto?.randomUUID
				? window.crypto.randomUUID()
				: `ardexa-${Date.now()}-${Math.random().toString(16).slice(2)}`;
			window.sessionStorage.setItem(storageKey, key);
			return key;
		};

		const getCart = async () => {
			const response = await fetch("/cart.js", {
				credentials: "same-origin",
				headers: { Accept: "application/json" },
			});
			if (!response.ok) throw new Error("cart_fetch_failed");
			return response.json();
		};

		const createSession = async (cart, sourcePage = "cart") => {
			const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
			if (!apiBase) throw new Error("checkout_api_missing");
			const response = await fetch(`${apiBase}/public/checkout/sessions`, {
				method: "POST",
				credentials: "include",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
					"X-Ardexa-Idempotency-Key": getIdempotencyKey(cart),
				},
				body: JSON.stringify({
					cart,
					source: {
						page: sourcePage,
						locale: config.locale || "it",
						checkoutPagePath: config.pagePath || "/pages/checkout-ardexa",
					},
				}),
			});
			if (!response.ok) throw new Error("checkout_session_create_failed");
			return response.json();
		};

		const getSession = async (sessionId) => {
			const apiBase = normalizeApiBaseUrl(config.apiBaseUrl);
			if (!apiBase) throw new Error("checkout_api_missing");
			const token = window.sessionStorage.getItem(
				buildCheckoutStorageKey(sessionId),
			);
			if (!token) throw new Error("checkout_token_missing");
			const response = await fetch(
				`${apiBase}/public/checkout/sessions/${encodeURIComponent(sessionId)}`,
				{
					credentials: "include",
					headers: {
						Accept: "application/json",
						Authorization: `Bearer ${token}`,
					},
				},
			);
			if (response.status === 410) throw new Error("checkout_session_expired");
			if (!response.ok) throw new Error("checkout_session_fetch_failed");
			return response.json();
		};

		const startCheckout = async (sourcePage = "cart") => {
			const cart = await getCart();
			if (
				!cart ||
				!cart.item_count ||
				!Array.isArray(cart.items) ||
				cart.items.length === 0
			) {
				throw new Error("cart_empty");
			}
			const session = await createSession(cart, sourcePage);
			window.sessionStorage.setItem(
				buildCheckoutStorageKey(session.id),
				session.token,
			);
			const checkoutPath = buildCheckoutPath(config.pagePath, session.id);
			if (!checkoutPath.startsWith("/"))
				throw new Error("checkout_path_invalid");
			window.location.assign(checkoutPath);
			return session;
		};

		const bindCheckoutButtons = () => {
			if (!config.enabled) return;
			document
				.querySelectorAll("[data-ardexa-checkout-trigger]")
				.forEach((button) => {
					if (button.dataset.ardexaCheckoutBound === "true") return;
					button.dataset.ardexaCheckoutBound = "true";
					button.addEventListener("click", async (event) => {
						event.preventDefault();
						if (button.disabled) return;
						button.setAttribute("aria-busy", "true");
						button.classList.add("is-loading");
						try {
							await startCheckout(
								button.dataset.ardexaCheckoutSource || "cart",
							);
						} catch (error) {
							button.removeAttribute("aria-busy");
							button.classList.remove("is-loading");
							window.dispatchEvent(
								new CustomEvent("ardexa:checkout:error", {
									detail: {
										error: String(error?.message || error),
									},
								}),
							);
							window.alert?.(
								"Non siamo riusciti ad aprire il checkout Ardexa. Riprova tra poco.",
							);
						}
					});
				});
		};

		window.ArdexaCheckoutBridge = {
			normalizeApiBaseUrl,
			buildCheckoutStorageKey,
			buildCheckoutPath,
			getSessionIdFromUrl,
			getCart,
			createSession,
			getSession,
			startCheckout,
			bindCheckoutButtons,
		};

		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", bindCheckoutButtons);
		} else {
			bindCheckoutButtons();
		}
	},
);
